/*
 * Lieferfenster auf der Produktseite (blocks/rxk-lieferzeit.liquid).
 *
 * Der Block liefert die Werktage schon fertig addiert (Bearbeitung + Transport)
 * und als Text fuer Besucher ohne JavaScript. Hier wird daraus ein echtes
 * Datum. Das passiert im Browser, weil Shopify Seiten zwischenspeichert —
 * ein serverseitiges "heute" waere schnell veraltet.
 *
 * Bestellschluss (data-schluss, Stunde deutscher Zeit): Werktags vorher zaehlt
 * heute als Bestelltag, danach und am Wochenende der naechste Werktag. Ab dem
 * Bestelltag zaehlen Bearbeitung und Transport. Ohne Schluss (0) wie bisher
 * ab heute. Werktage sind
 * Mo–Fr ohne Feiertage in NRW, dem Versandstandort Moenchengladbach.
 * Feiertage im Empfaengerland kennt der Block nicht — deshalb "voraussichtlich".
 */

const BERLIN = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Aktuelle Uhrzeit in Deutschland als Minuten seit Mitternacht. */
const BERLIN_ZEIT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Berlin',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
function minutenBerlin() {
  const [stunde, minute] = BERLIN_ZEIT.format(new Date()).split(':').map(Number);
  return stunde * 60 + minute;
}

/** Aktuelle Stunde in Deutschland, 0–23. */
function stundeBerlin() {
  return Math.floor(minutenBerlin() / 60);
}

/** Heutiges Kalenderdatum in Deutschland, als UTC-Mitternacht. */
function heute() {
  const [j, m, t] = BERLIN.format(new Date()).split('-').map(Number);
  return new Date(Date.UTC(j, m - 1, t));
}

function tagePlus(datum, tage) {
  const neu = new Date(datum);
  neu.setUTCDate(neu.getUTCDate() + tage);
  return neu;
}

/** Ostersonntag nach der Gauss'schen Osterformel (anonymer gregorianischer Algorithmus). */
function ostersonntag(jahr) {
  const a = jahr % 19;
  const b = Math.floor(jahr / 100);
  const c = jahr % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const monat = Math.floor((h + l - 7 * m + 114) / 31);
  const tag = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(jahr, monat - 1, tag));
}

const feiertagsCache = new Map();

/** Gesetzliche Feiertage NRW als Set von "JJJJ-MM-TT". */
function feiertage(jahr) {
  if (feiertagsCache.has(jahr)) return feiertagsCache.get(jahr);
  const ostern = ostersonntag(jahr);
  const tage = [
    new Date(Date.UTC(jahr, 0, 1)), // Neujahr
    tagePlus(ostern, -2), // Karfreitag
    tagePlus(ostern, 1), // Ostermontag
    new Date(Date.UTC(jahr, 4, 1)), // Tag der Arbeit
    tagePlus(ostern, 39), // Christi Himmelfahrt
    tagePlus(ostern, 50), // Pfingstmontag
    tagePlus(ostern, 60), // Fronleichnam (NRW)
    new Date(Date.UTC(jahr, 9, 3)), // Tag der Deutschen Einheit
    new Date(Date.UTC(jahr, 10, 1)), // Allerheiligen (NRW)
    new Date(Date.UTC(jahr, 11, 25)), // 1. Weihnachtstag
    new Date(Date.UTC(jahr, 11, 26)), // 2. Weihnachtstag
  ];
  const set = new Set(tage.map((t) => t.toISOString().slice(0, 10)));
  feiertagsCache.set(jahr, set);
  return set;
}

function istWerktag(datum) {
  const wochentag = datum.getUTCDay();
  if (wochentag === 0 || wochentag === 6) return false;
  return !feiertage(datum.getUTCFullYear()).has(datum.toISOString().slice(0, 10));
}

/**
 * Wie viele Werktage liegen zwischen zwei Tagen? Fuer die Pause: Wir fertigen
 * erst ab dem Wiederbeginn, also muessen alle Fenster um so viele Werktage
 * nach hinten wandern, wie bis dahin vergehen.
 */
function werktageBis(von, bis) {
  let tag = von;
  let anzahl = 0;
  while (tag < bis && anzahl < 400) {
    tag = tagePlus(tag, 1);
    if (istWerktag(tag)) anzahl++;
  }
  return anzahl;
}

function werktagePlus(datum, anzahl) {
  let tag = datum;
  let gezaehlt = 0;
  while (gezaehlt < anzahl) {
    tag = tagePlus(tag, 1);
    if (istWerktag(tag)) gezaehlt++;
  }
  return tag;
}

function datumsformat(mitWochentag) {
  return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
    ...(mitWochentag && { weekday: 'short' }),
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/** Ab wann aus dem Bestellschluss-Satz die verbleibende Zeit wird (Minuten). */
const COUNTDOWN_AB = 180;

class RxkLieferfenster extends HTMLElement {
  #takt = 0;
  #sektion = null;
  #lief = false;
  // Zusaetzliche Fertigungstage der gewaehlten Add-ons, zuletzt eingerechnet.
  #addonTage = 0;
  // Steht in der Bestellschluss-Zeile der Pausensatz? Dann nicht ueberschreiben.
  #pauseSatz = false;
  #basisTage = null;

  connectedCallback() {
    this.hidden = true;
    this.#basisTage ??= Number(this.dataset.bearbeitung) || 0;
    this.#sektion = this.closest('.shopify-section, dialog');
    this.#sektion?.addEventListener('shopify:product:select', this.#variantenwechsel);
    document.addEventListener('rxk:fertigungstage', this.#fertigungstage);

    this.#fenstersetzen();

    this.#auslastung();

    if (this.dataset.stil === 'satz') return;
    this.#kostenUmrechnen();

    const schluss = Number(this.dataset.schluss) || 0;
    const schlussFeld = this.querySelector('[data-schluss-zeile]');
    if (schlussFeld && schluss) this.#bestellschluss(schlussFeld, schluss, Number(this.dataset.bearbeitung) || 0);
  }

  /**
   * Bearbeitungszeit aus dem Konfigurator-Backend (20.09.2026).
   *
   * Im Theme steht der Normalwert, im Backend der aktuelle — er haengt an der
   * Auslastung der Werkstatt und soll ohne Theme-Bearbeitung aenderbar sein.
   * Liegt er hoeher, wandern alle Fenster um die Differenz nach hinten und ein
   * Hinweis erscheint. Faellt die Abfrage aus, bleibt der Theme-Wert stehen:
   * lieber der Normalwert als ein leeres Lieferfenster.
   *
   * Die Antwort liegt 2 Minuten im sessionStorage, damit nicht jede
   * Produktseite neu fragt.
   */
  async #auslastung() {
    const url = this.dataset.bearbeitungUrl;
    if (!url) return;
    const schluessel = 'rxk-bearbeitung';
    let daten = null;
    try {
      const gemerkt = JSON.parse(sessionStorage.getItem(schluessel) || 'null');
      if (gemerkt && Date.now() - gemerkt.at < 2 * 60 * 1000) daten = gemerkt.wert;
    } catch (e) { /* kein Speicher, dann eben frisch holen */ }

    if (!daten) {
      try {
        // Abbruch nach 4 s, aber nur wo der Browser es kann: AbortSignal.timeout
        // gibt es erst ab Chrome 103 / Safari 16. In aelteren Browsern waere der
        // Aufruf sonst still fehlgeschlagen und die Bearbeitungszeit haette
        // nie gegriffen.
        const abbruch = typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? { signal: AbortSignal.timeout(4000) } : {};
        const antwort = await fetch(url, abbruch);
        if (!antwort.ok) return;
        daten = await antwort.json();
        try { sessionStorage.setItem(schluessel, JSON.stringify({ at: Date.now(), wert: daten })); } catch (e) { /* egal */ }
      } catch (e) { return; }
    }

    const normal = this.#basisTage;
    const jetzt = daten && daten.werktage != null ? Number(daten.werktage) : normal;
    if (!Number.isSafeInteger(jetzt) || jetzt < 0) return;

    // Pause (Urlaub): Gefertigt wird erst ab dem angegebenen Tag. Alles wandert
    // um die Werktage nach hinten, die bis dahin vergehen — sonst verspricht das
    // Lieferfenster Termine, an denen niemand in der Werkstatt steht.
    let pauseZusatz = 0;
    const heuteIso2 = new Date().toISOString().slice(0, 10);
    if (daten.pause_bis && daten.pause_bis >= heuteIso2) {
      const schlussP = Number(this.dataset.schluss) || 0;
      const tagP = heute();
      const startP = !schlussP || (istWerktag(tagP) && stundeBerlin() < schlussP) ? tagP : werktagePlus(tagP, 1);
      pauseZusatz = werktageBis(startP, new Date(daten.pause_bis + 'T00:00:00Z'));
    }

    const zusatz = jetzt - normal + pauseZusatz;
    // Steht die Aussage schon in der Bestellschluss-Zeile, faellt die
    // Hinweiszeile darunter weg — sonst steht zweimal dasselbe da.
    let imSchluss = false;
    if (zusatz !== 0) {
      const d = this.dataset;
      for (const feld of ['min', 'max', 'expressMin', 'expressMax', 'bearbeitung']) {
        if (d[feld] !== undefined && d[feld] !== '') d[feld] = String(Number(d[feld]) + zusatz);
      }
      this.#fenstersetzen();
      const schluss = Number(d.schluss) || 0;
      const schlussFeld = this.querySelector('[data-schluss-zeile]');
      if (schlussFeld && schluss) {
        // Der erste Aufruf laeuft schon mit dem alten Satz im Takt — erst stoppen,
        // sonst schreiben zwei Timer abwechselnd in dieselbe Zeile.
        clearInterval(this.#takt);
        if (pauseZusatz > 0) {
          // Waehrend der Pause waere ein Bestellschluss irrefuehrend: Heute bis
          // 13 Uhr bestellt geht trotzdem erst nach der Pause raus.
          const datumP = new Intl.DateTimeFormat(document.documentElement.lang || 'de', { day: 'numeric', month: 'long' })
            .format(new Date(daten.pause_bis + 'T00:00:00Z'));
          schlussFeld.textContent = (d.textPause || '').replace('__DATUM__', datumP);
          imSchluss = true;
          this.#pauseSatz = true;
        } else {
          // Der Satz darunter sagt sonst weiter "Versand am naechsten Werktag".
          if (d.textSchlussTage) {
            schlussFeld.textContent = d.textSchlussTage.replace('__TAGE__', String(d.bearbeitung));
            imSchluss = true;
          }
          this.#bestellschluss(schlussFeld, schluss, Number(d.bearbeitung) || 0);
        }
      }
    }

    this.hidden = false;
    const hinweisFeld = this.querySelector('[data-auslastung]');
    if (!hinweisFeld || daten.hinweis === false || imSchluss) return;
    const heuteIso = new Date().toISOString().slice(0, 10);
    if (daten.pause_bis && daten.pause_bis >= heuteIso && this.dataset.textPause) {
      const datum = new Intl.DateTimeFormat(document.documentElement.lang || 'de', { day: 'numeric', month: 'long' })
        .format(new Date(daten.pause_bis + 'T00:00:00Z'));
      hinweisFeld.textContent = this.dataset.textPause.replace('__DATUM__', datum);
      hinweisFeld.hidden = false;
    } else if (zusatz > 0 && this.dataset.textAuslastung) {
      hinweisFeld.textContent = this.dataset.textAuslastung.replace('__TAGE__', String(jetzt));
      hinweisFeld.hidden = false;
    }
  }

  /** Traegt die Daten ein. Laeuft nach dem Bestellschluss noch einmal. */
  #fenstersetzen() {
    const d = this.dataset;
    const schluss = Number(d.schluss) || 0;
    const tag = heute();
    const start = !schluss || (istWerktag(tag) && stundeBerlin() < schluss) ? tag : werktagePlus(tag, 1);
    const format = datumsformat(d.stil === 'satz');
    // Ein einzelnes Datum bekommt den Wochentag ("Do., 18. Sept."): bei Express
    // steht dort sonst nur eine nackte Zahl, und „Donnerstag" beantwortet die
    // eigentliche Frage schneller. In einer Spanne waere er doppelt und lang.
    const formatEinzel = datumsformat(true);

    const fenster = (min, max) => {
      const von = werktagePlus(start, min);
      const bis = werktagePlus(start, max);
      if (min === max) return formatEinzel.format(von);
      if (typeof format.formatRange !== 'function') return `${format.format(von)} – ${format.format(bis)}`;
      return format.formatRange(von, bis);
    };

    if (d.stil === 'satz') {
      this.#satz(fenster);
      return;
    }

    const spannen = {
      standard: [d.min, d.max],
      express: [d.expressMin, d.expressMax],
      versand: [d.bearbeitung, d.bearbeitung],
    };

    // In "spalten" steht "Voraussichtlich bei dir" darueber — ein "ca." am Datum waere doppelt.
    const vorlage = d.stil === 'spalten' ? '__FENSTER__' : d.textCa;
    for (const feld of this.querySelectorAll('[data-fenster]')) {
      const [min, max] = spannen[feld.dataset.fenster] || [];
      if (min === undefined || min === '' || max === '') continue;
      feld.textContent = vorlage.replace('__FENSTER__', fenster(Number(min), Number(max)));
    }
  }

  disconnectedCallback() {
    clearInterval(this.#takt);
    this.#sektion?.removeEventListener('shopify:product:select', this.#variantenwechsel);
    document.removeEventListener('rxk:fertigungstage', this.#fertigungstage);
  }

  /**
   * Zusaetzliche Fertigungstage aus dem Konfigurator (20.09.2026).
   *
   * Der Konfigurator meldet die Summe der gewaehlten Add-ons, sobald der Kunde
   * eines an- oder abwaehlt. Gerechnet wird mit dem Unterschied zum zuletzt
   * eingerechneten Wert — so stimmt das Fenster auch, wenn er ein Add-on
   * wieder abwaehlt, ohne dass die Seite neu laden muesste.
   *
   * Die Bearbeitungszeit aus dem Backend (#auslastung) hat die Felder da schon
   * angefasst; beide Zuschlaege addieren sich, was richtig ist: Hohe Auslastung
   * UND ein aufwendiges Add-on dauern zusammen laenger.
   */
  #fertigungstage = (ereignis) => {
    const neu = Math.max(0, Number(ereignis?.detail?.tage) || 0);
    const unterschied = neu - this.#addonTage;
    if (!unterschied) return;
    this.#addonTage = neu;

    const d = this.dataset;
    for (const feld of ['min', 'max', 'expressMin', 'expressMax', 'bearbeitung']) {
      if (d[feld] !== undefined && d[feld] !== '') d[feld] = String(Math.max(0, Number(d[feld]) + unterschied));
    }
    this.#fenstersetzen();

    if (this.dataset.stil === 'satz' || this.#pauseSatz) return;
    const schluss = Number(d.schluss) || 0;
    const schlussFeld = this.querySelector('[data-schluss-zeile]');
    if (!schlussFeld || !schluss) return;

    // Der Takt laeuft noch mit dem alten Satz — erst stoppen, sonst schreiben
    // zwei Zeitgeber abwechselnd in dieselbe Zeile.
    clearInterval(this.#takt);
    const tage = Number(d.bearbeitung) || 0;
    if (tage > 1 && d.textSchlussTage) schlussFeld.textContent = d.textSchlussTage.replace('__TAGE__', String(tage));
    else if (d.textSchluss) schlussFeld.textContent = d.textSchluss;
    this.#bestellschluss(schlussFeld, schluss, tage);
  };

  /**
   * Variantenwechsel: Horizon rendert diesen Block nicht neu, die nachgeladene
   * Seite enthaelt ihn aber. Ist die neue Variante ausverkauft, verschwindet
   * das Lieferfenster — sonst stuende unter "Ausverkauft" ein Lieferdatum.
   * Dieselbe Weiche wie in Liquid, nur aus dem geholten HTML gelesen.
   */
  #variantenwechsel = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const neu = detail?.html?.querySelector('rxk-lieferfenster');
        const huelle = this.closest('.rxk-lz');
        if (!neu || !huelle) return;
        huelle.hidden = neu.dataset.verfuegbar !== 'true';
      })
      .catch(() => {});
  };

  /**
   * Bestellschluss unter den Spalten. Im Normalfall der feste Satz; in den
   * letzten Stunden davor die verbleibende Zeit mit dem Versandtag — dann ist
   * sie die Information, auf die es ankommt. Die Zeit kommt aus deutscher
   * Zeitzone, nicht aus der Uhr des Besuchers.
   */
  #bestellschluss(feld, schluss, bearbeitung) {
    const satz = feld.textContent;
    const format = datumsformat(true);
    const d = this.dataset;

    const zeigen = () => {
      const tag = heute();
      const rest = schluss * 60 - minutenBerlin();
      if (!istWerktag(tag) || rest <= 0 || rest > COUNTDOWN_AB) {
        feld.textContent = satz;
        // Wer beim Ablauf zusieht, darf danach nicht die alten Daten lesen:
        // ab jetzt zaehlt der naechste Werktag als Bestelltag.
        if (this.#lief) {
          this.#lief = false;
          this.#fenstersetzen();
        }
        return;
      }
      this.#lief = true;
      const stunden = Math.floor(rest / 60);
      const zeit = stunden
        ? d.textStunden.replace('__H__', stunden).replace('__M__', rest % 60)
        : d.textMinuten.replace('__M__', rest);
      const versand = bearbeitung ? werktagePlus(tag, bearbeitung) : tag;
      feld.textContent = d.textCountdown.replace('__ZEIT__', zeit).replace('__TAG__', format.format(versand));
    };

    zeigen();
    this.#takt = setInterval(zeigen, 60000);
  }

  /**
   * Versandkosten in fremder Waehrung. Der Block liefert Euro-Cent, Shopify
   * stellt den Tageskurs in Shopify.currency bereit. Shopify rechnet
   * Versandtarife genau so um und rundet nur auf die kleinste Einheit
   * (gemessen: 10,90 EUR x 1,1777 = $12.84) — anders als Produktpreise, die
   * auf glatte Betraege gerundet werden. In der Shopwaehrung steht der
   * Betrag schon im HTML.
   */
  #kostenUmrechnen() {
    const felder = this.querySelectorAll('[data-kosten-cent]');
    const waehrung = window.Shopify && window.Shopify.currency;
    if (!felder.length || !waehrung || waehrung.active === this.dataset.shopWaehrung) return;
    const kurs = Number(waehrung.rate);
    if (!kurs) return;

    let format;
    try {
      // Englisches Zahlenformat wie Shopifys Waehrungsformate (¥10,100, CHF 54.00),
      // unabhaengig von der Seitensprache — "ja" lieferte sonst das breite ￥.
      format = new Intl.NumberFormat('en', {
        style: 'currency',
        currency: waehrung.active,
        currencyDisplay: 'narrowSymbol',
      });
    } catch {
      return; // unbekannte Waehrung: lieber kein Betrag als ein falscher
    }
    const stellen = format.resolvedOptions().maximumFractionDigits;
    for (const feld of felder) {
      const betrag = (Number(feld.dataset.kostenCent) / 100) * kurs;
      feld.textContent = format.format(Number(betrag.toFixed(stellen)));
    }
  }

  #satz(fenster) {
    const d = this.dataset;
    const standard = d.min && d.max ? fenster(Number(d.min), Number(d.max)) : '';
    const express =
      d.expressMin && d.expressMax
        ? d.textExpress.replace('__FENSTER__', fenster(Number(d.expressMin), Number(d.expressMax)))
        : '';

    // Ohne Standardzeit steht Express direkt hinter dem Land, wie im Text ohne JavaScript.
    if (standard) {
      this.textContent = d.textStandard.replace('__FENSTER__', standard) + (express ? ` · ${express}` : '');
    } else if (express) {
      this.textContent = d.textStandard.replace('__FENSTER__', express);
    }
  }
}

if (!customElements.get('rxk-lieferfenster')) {
  customElements.define('rxk-lieferfenster', RxkLieferfenster);
}
